import {
  applySchedule as coreApplySchedule,
  captureBaseline as coreCaptureBaseline,
  saveBaseline as coreSaveBaseline,
  schedule as coreSchedule,
  type Baseline,
  type BaselineNumber,
  type Diagnostic,
  type Project,
  type SaveBaselineOptions,
  type ScheduleInput,
  type ScheduleResult,
} from '@bigint-project/core';

/**
 * 引擎呼叫的降級包裝層。
 *
 * `packages/core` 的排程／基準線引擎由其他 agent 平行實作中，完成時間點
 * 不受這裡控制。這一層的存在意義就是：任何一次呼叫都不可以讓 app 崩潰 ——
 * 失敗時（不論是尚未實作、或是實作完成後拋出的正常業務錯誤，例如
 * 相依關係成環）記錄成 diagnostic、回傳原始 project 未變動；成功時把結果
 * 套用回去。
 *
 * `schedule`/`applySchedule`/`captureBaseline`/`saveBaseline` 參數皆可覆寫
 * （預設為 `packages/core` 的真實實作），讓測試可以注入固定行為的 stub，
 * 不需要依賴「引擎目前到底實作到哪」這種會隨時間改變的前提。
 */

export interface EngineRunResult {
  readonly project: Project;
  readonly diagnostics: readonly Diagnostic[];
  readonly error: string | null;
}

/**
 * 安全地把任意拋出值轉成訊息字串。
 * `String(error)` 本身可能再拋出（例如物件的 `toString`/`Symbol.toStringTag`
 * 被刻意寫成會丟例外），若不擋下來會讓「永不拋出」的包裝層失守。
 */
function toMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  try {
    return String(error);
  } catch {
    return 'Unknown error (failed to stringify)';
  }
}

export interface RunScheduleDeps {
  readonly schedule: (input: ScheduleInput) => ScheduleResult;
  readonly applySchedule: (project: Project, result: ScheduleResult) => Project;
}

const defaultScheduleDeps: RunScheduleDeps = {
  schedule: coreSchedule,
  applySchedule: coreApplySchedule,
};

/**
 * 執行 CPM 排程並套用結果。
 * 任何拋出（尚未實作、或已實作後的正常錯誤）都被吞掉，回傳原始 project 與一則 diagnostic。
 */
export function runSchedule(project: Project, deps: RunScheduleDeps = defaultScheduleDeps): EngineRunResult {
  try {
    const result = deps.schedule({ project });
    const nextProject = deps.applySchedule(project, result);
    return { project: nextProject, diagnostics: result.diagnostics, error: null };
  } catch (error) {
    return {
      project,
      diagnostics: [
        {
          severity: 'Warning',
          code: 'SCHEDULE_ENGINE_UNAVAILABLE',
          message: `排程失敗，暫不套用：${toMessage(error)}`,
        },
      ],
      error: toMessage(error),
    };
  }
}

export interface BaselineRunResult {
  readonly project: Project;
  readonly baseline: Baseline | null;
  readonly diagnostics: readonly Diagnostic[];
  readonly error: string | null;
}

export interface RunSetBaselineDeps {
  readonly captureBaseline: (project: Project, options: SaveBaselineOptions) => Baseline;
  readonly saveBaseline: (project: Project, baseline: Baseline) => Project;
}

const defaultBaselineDeps: RunSetBaselineDeps = {
  captureBaseline: coreCaptureBaseline,
  saveBaseline: coreSaveBaseline,
};

/** 擷取並儲存基準線；引擎未就緒或失敗時優雅降級。 */
export function runSetBaseline(
  project: Project,
  options: SaveBaselineOptions,
  deps: RunSetBaselineDeps = defaultBaselineDeps,
): BaselineRunResult {
  try {
    const baseline = deps.captureBaseline(project, options);
    const nextProject = deps.saveBaseline(project, baseline);
    return { project: nextProject, baseline, diagnostics: [], error: null };
  } catch (error) {
    return {
      project,
      baseline: null,
      diagnostics: [
        {
          severity: 'Warning',
          code: 'BASELINE_ENGINE_UNAVAILABLE',
          message: `基準線設定失敗：${toMessage(error)}`,
        },
      ],
      error: toMessage(error),
    };
  }
}

export type { BaselineNumber };
